import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import {
    Box,
    Divider,
    Typography,
    Stack,
    MenuItem,
    Popover,
    Button,
    Slide
} from '@mui/material';
// hooks
import useResponsive from 'hooks/useResponsive';
// server
import i18n from 'i18';

import ChangePassDialog from 'pages/dialogs/ChangePassDialog';
import UpdateProfileDialog from '../pages/dialogs/UpdateProfileDialog';
import { useStateSelector } from '../context/StateSelectors';
import {
    ArrowDownBoldIcon,
    ArrowUpBoldIcon,
    Edit1LinearIcon,
    KeySquareLinearIcon
} from '../icons-lib';
import LanguagePopover from './LanguagePopover';
import config from '../config';
import { Skeleton } from '@mui/material';

const StyledAccount = styled(Button)(({ theme }) => ({
    "justifyContent": 'space-between',
    "width": '100%',
    "maxWidth": '240px',
    '&:hover': {
        backgroundColor: theme.palette.background.default
    }
}));

const baseUsersHead = [
    { id: 'firstName', label: 'firstName', alignRight: false, cell: 'Text', required: true },
    { id: 'lastName', label: 'lastName', alignRight: false, cell: 'Text', required: true },
    {
        id: 'gender',
        label: 'gender',
        alignRight: false,
        cell: 'Items',
        items: ['male', 'female']
    },
    { id: 'birthDate', label: 'birthDate', alignRight: false, cell: 'Date' }
];

export default function AccountPopover() {
    const server = useStateSelector((state) => state.server);
    const userInfo = useStateSelector((state) => state.userInfo);

    const [open, setOpen] = useState(null);
    const [changePassDialogOpen, setChangePassDialogOpen] = useState(false);
    const [profileDialogOpen, setProfileDialogOpen] = useState(false);

    const navigate = useNavigate();

    const handleOpen = (event) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    const handleLogout = async () => {
        await server.auth.logoutUser();
        navigate('/auth/login', { replace: true });
    };

    const isDesktop = useResponsive('up', 'lg');

    const currentUserData = userInfo ? userInfo : {};
    const displayName =
        (currentUserData?.firstName || '') +
        (currentUserData?.lastName ? ' ' + currentUserData.lastName : '');

    const email = currentUserData?.email;

    const isTwoFactorEnabled = () => {
        const allowedRoles = config.allowTwoFactorAuth;
        const currentSystemRole = currentUserData?.role?.systemRole;
        return allowedRoles.includes(currentSystemRole);
    };

    const UsersHead = [
        ...baseUsersHead,
        ...(isTwoFactorEnabled()
            ? [
                {
                    id: 'twoFactorEnabled',
                    label: 'twoFactorEnabled',
                    alignRight: false,
                    cell: 'Dialog2FA'
                }
            ]
            : [])
    ];

    return (
        <>
            <ChangePassDialog
                dialogOpen={changePassDialogOpen}
                userId={currentUserData.id}
                onClose={() => setChangePassDialogOpen(false)}
            />

            {currentUserData.id && (
                <UpdateProfileDialog
                    dialogOpen={profileDialogOpen}
                    dialogData={currentUserData}
                    Head={UsersHead}
                    onSave={(data) => {
                        setProfileDialogOpen(false);
                    }}
                    onClose={() => setProfileDialogOpen(false)}
                />
            )}

            <StyledAccount
                sx={
                    isDesktop
                        ? { cursor: 'pointer', mb: 5, mx: 2.5 }
                        : { cursor: 'pointer', mx: 2.5, mt: 5, mb: 1 }
                }
                onClick={handleOpen}
                disableRipple
                color="secondary"
                variant="outlined"
                endIcon={
                    <ArrowUpBoldIcon sx={{ width: '16px', height: '16px' }} />
                }
            >
                <Box
                    display="flex"
                    alignItems="flex-start"
                    flexDirection="column"
                >
                    {displayName ? (
                        <Typography variant="subtitle2">
                            {displayName}
                        </Typography>
                    ) : (
                        <Skeleton
                            width={150}
                            variant="text"
                            sx={{ fontSize: '1rem' }}
                        />
                    )}
                    {email ? (
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                        >
                            {email}
                        </Typography>
                    ) : (
                        <Skeleton
                            width={100}
                            variant="text"
                            sx={{ fontSize: '1rem' }}
                        />
                    )}
                </Box>
            </StyledAccount>

            <Popover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
                slotProps={{
                    paper: {
                        sx: {
                            "p": 0,
                            "width": '240px',
                            '& .MuiMenuItem-root': {
                                typography: 'body2',
                                borderRadius: 0.75
                            }
                        }
                    }
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ my: 1.5, px: 1.5 }}
                >
                    <Box display="flex" flexDirection="column">
                        {displayName ? (
                            <Typography variant="subtitle2" noWrap>
                                {displayName}
                            </Typography>
                        ) : (
                            <Skeleton
                                width={150}
                                variant="text"
                                sx={{ fontSize: '1rem' }}
                            />
                        )}
                        {email ? (
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                                noWrap
                            >
                                {email}
                            </Typography>
                        ) : (
                            <Skeleton
                                width={100}
                                variant="text"
                                sx={{ fontSize: '1rem' }}
                            />
                        )}
                    </Box>

                    <ArrowDownBoldIcon
                        onClick={handleClose}
                        sx={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                        }}
                    />
                </Box>

                <Divider />

                <Box
                    sx={{
                        p: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="subtitle2">
                        {i18n.t('profile.language')}
                    </Typography>
                    <LanguagePopover />
                </Box>

                <Divider />

                <Stack sx={{ p: 1 }}>
                    {/*<MenuItem*/}
                    {/*    key="home"*/}
                    {/*    onClick={() => {*/}
                    {/*        handleClose();*/}
                    {/*        navigate('/app');*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    {i18n.t('profile.home')}*/}
                    {/*</MenuItem>*/}

                    <MenuItem
                        key="profile"
                        onClick={() => {
                            handleClose();
                            setProfileDialogOpen(true);
                        }}
                    >
                        <Edit1LinearIcon sx={{ mr: 1 }} />
                        {i18n.t('profile.profile')}
                    </MenuItem>

                    <MenuItem
                        key="changePass"
                        onClick={() => {
                            handleClose();
                            setChangePassDialogOpen(true);
                        }}
                    >
                        <KeySquareLinearIcon sx={{ mr: 1 }} />
                        {i18n.t('buttons.changePass')}
                    </MenuItem>
                </Stack>

                <Divider />

                <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
                    {i18n.t('profile.logout')}
                </MenuItem>
            </Popover>
        </>
    );
}
